import { createRoot } from 'react-dom/client';
import React from 'react';
import 'react-date-picker/dist/DatePicker.css';
import LocalDate, { formattedDateString, parseId } from './utils.js';
import DefaultLayout from './DefaultLayout.mjs';
import RowCardsLayout from './RowCardsLayout.mjs';
import ErrorPlaceholder from './ErrorPlaceholder.mjs';
import WindCreekClient from '../../../src/scripts/user-account/WindCreekClient.mjs';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPlaceholder />;
    }

    return this.props.children;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const appDiv = document.getElementById('promotionsApp');
  const calendarTypeId = parseId(appDiv.dataset.calendarTypeId);
  const venueId = parseId(appDiv.dataset.venueId);
  const layout = appDiv.dataset.layout ?? 'default';
  const rowCardHtag = appDiv.dataset.rowCardHtag ?? 'h5';
  let propertyCode = WindCreekData.propertyCode;
  const propertyUrl = WindCreekData.propertyURL;
  const promoTypes = JSON.parse(appDiv.dataset.promotionTypes);
  const promoPerks = JSON.parse(appDiv.dataset.promotionPerks);
  const propertyPerks = JSON.parse(appDiv.dataset.propertyPerks);
  const versePerks = JSON.parse(appDiv.dataset.versePerks);
  const showUnpublished = JSON.parse(appDiv.dataset.showUnpublished) ?? false;
  const currentMonthObj = new LocalDate();
  const nextMonthObj = new LocalDate(currentMonthObj.nextMonthDateString());
  const minValue = currentMonthObj.date;
  const maxValue = currentMonthObj.nextYear();

  const root = createRoot(appDiv);

  // Aruba exception, since its code is `ARUBA` for the promotions only
  if (propertyCode === 'ACC') {
    propertyCode = 'ARUBA';
  }
  if (propertyCode === 'WCC') {
    propertyCode = 'WCI';
  }
  // TODO: Temporary. Make MCC be WCA for now just to see content
  if (propertyCode === 'MCC') {
    propertyCode = 'WCA';
  }
  // API & Promos
  const wcc = new WindCreekClient();
  const queryBody = {
    operationName: "GetCalendar",
    variables: {
      input: {
        property: propertyCode,
        startDate: formattedDateString(minValue),
        endDate: formattedDateString(maxValue),
        showUnpublishedEvents: showUnpublished,
        showEmployeeOnlyEvents: null,
        calendarTypeId:calendarTypeId,
        venueId: venueId,
      }
    },
    query: `query GetCalendar($input: GetCommunityEventsCalendar!) {
      communityEventsCalendar(input: $input) {
        calendarDays {
          date
          eventIds
          __typename
        }
        earnDays {
          date
          eventIds
          __typename
        }
        events {
          category {
            eventCategoryId
            displayName
            value
            lastModified
            __typename
          }
          createdDate
          detailsUrl
          earnPeriodDescription
          earnPeriodEndDate
          earnPeriodEndTime
          earnPeriodImageUrl
          earnPeriodRecurringPattern {
            recurringType
            separationCount
            maxNumberOfOccurrences
            daysOfWeek
            weekOfMonth
            dayOfMonth
            monthOfYear
            __typename
          }
          earnPeriodStartDate
          earnPeriodStartTime
          enabled
          endDate
          endTime
          eventId
          featuredImageUrl
          howToQualifyHeader
          imageAlt
          imageUrl
          isEarnPeriodRecurring
          isEmployeeOnly
          isFullDayEvent
          isRecurring
          lastModified
          name
          occurrenceDates
          occurrenceDetails
          parentEventId
          promoPerks {
            id
            name
            value
            __typename
          }
          promotionTypes
          property
          recurringPattern {
            recurringType
            separationCount
            maxNumberOfOccurrences
            daysOfWeek
            weekOfMonth
            dayOfMonth
            monthOfYear
            __typename
          }
          shortDescription
          shortDescriptionHeader
          startDate
          startTime
          thumbnailImageUrl
          venueId
          versePerks {
            id
            name
            value
            __typename
          }
          __typename
        }
        __typename
      }
    }`
  };

  wcc.query(queryBody)
    .then(response => {
      const data = response.data.communityEventsCalendar;
      const calendarDays = data.calendarDays;
      const earnDays = data.earnDays;
      const events = data.events;

      // Merge Calendar and Earn days
      let days = [...calendarDays, ...earnDays];
      // Removes duplicates
      let mergedDays = days.reduce((map, obj) => {
        map[obj.date] = map[obj.date] ? [...new Set([...map[obj.date], ...obj.eventIds])] : obj.eventIds;
        return map;
      }, {});
      // Maps back to the original data structure
      let mappedDays = Object.keys(mergedDays).map(date => ({
        date: date,
        eventIds: mergedDays[date]
      }));

      // Map the event details to the eventIds
      let mappedCalendarDays = mappedDays.map(day => {
        if (events) {
          let dayEvents = day.eventIds.map(eventId =>
            events.find(event => event.eventId === eventId)
          );

          return { ...day, events: dayEvents };
        }

        return false;
      });

      let appToRender;
      switch (layout) {
        case 'row-cards':
          appToRender = (
            <RowCardsLayout
              propertyUrl={propertyUrl}
              currentMonthObj={currentMonthObj}
              minValue={minValue}
              maxValue={maxValue}
              mappedCalendarDays={mappedCalendarDays}
              rowCardHtag={rowCardHtag}
            />
          );
          break;
        case 'no-calendars':
        default:
          appToRender = (
            <DefaultLayout
              appDiv={appDiv}
              promoTypes={promoTypes}
              promoPerks={promoPerks}
              propertyPerks={propertyPerks}
              versePerks={versePerks}
              currentMonthObj={currentMonthObj}
              nextMonthObj={nextMonthObj}
              minValue={minValue}
              maxValue={maxValue}
              mappedCalendarDays={mappedCalendarDays}
            />
          );
      }

      root.render(
        <ErrorBoundary>
          {appToRender}
        </ErrorBoundary>
      );
    })
    .catch(error => {
      console.error('Error:', error);

      root.render(
        <ErrorBoundary>
          <ErrorPlaceholder />
        </ErrorBoundary>
      );
    });
});
